import { ActionType } from "../CommonProps";
import { CurrentUser } from "../modals/CurrentUser";
import { ForgotPasswordRequest } from "../modals/ForgotPasswordRequest";
import { ICarbizReferralPayload } from "../modals/Interface";
import { CommonMethodService } from "./CommonMethodService";

const apiAdBaseUrl = "https://adonis.flowautobody.com/api/";
// const apiAdBaseUrl = 'http://127.0.0.1:3333/api/';
const apiAdBase = "https://adonis.flowautobody.com/";
// const apiAdBase = 'http://127.0.0.1:3333/';
// const apiBaseUrl = "https://api.flowautobody.com" //aws
const apiBaseUrl = "https://flowautobody.azurewebsites.net"; //azure
const apiIbsAdBaseUrl = "https://ibsapi.flowautobody.com/";
// const apiIbsAdBaseUrl = "http://127.0.0.1:3331/";

export const allowedEmails = [
  "isko20951@gmail.com",
  "tahir60652@gmail.com",
  "tb.crm12@gmail.com",
  "renatofrias@outlook.com",
];

export class APIService {
  private currentUser: CurrentUser;
  private commonMethodService = new CommonMethodService();

  constructor() {
    this.currentUser =
      this.commonMethodService.getDataFromStorage("currentUser");
  }

  sendRequest(url: string, method: string, data: any) {
    let requestOptions: any = {
      method: method,
      headers: { "Content-Type": "application/json" },
    };
    if (data) {
      requestOptions["body"] = JSON.stringify(data);
    }

    return fetch(apiBaseUrl + url, requestOptions);
  }

  private sendRequestApi(
    url: string,
    method: string,
    data?: any,
    token?: string
  ) {
    let requestOptions: any = {
      method: method,
      headers: { "Content-Type": "application/json" },
    };
    if (data) {
      requestOptions["body"] = JSON.stringify(data);
    }
    if (this.currentUser?.auth || token) {
      requestOptions["headers"] = {
        ...requestOptions["headers"],
        Authorization: `Bearer ${token ? token : this.currentUser.auth.token}`,
      };
    }

    return fetch(apiAdBaseUrl + url, requestOptions);
  }

  private sendRequestIbsApi(
    url: string,
    method: string,
    data?: any,
    token?: string
  ) {
    let requestOptions: any = {
      method: method,
      headers: { "Content-Type": "application/json" },
    };
    if (data) {
      requestOptions["body"] = JSON.stringify(data);
    }
    if (this.currentUser?.auth || token) {
      requestOptions["headers"] = {
        ...requestOptions["headers"],
        Authorization: `Bearer ${token ? token : this.currentUser.auth.token}`,
      };
    }

    return fetch(apiIbsAdBaseUrl + url, requestOptions);
  }

  sendRequestAdonisApi(url: string, method: string, data: any) {
    let requestOptions: any = {
      method: method,
      headers: { "Content-Type": "application/json" },
    };
    if (data) {
      requestOptions["body"] = JSON.stringify(data);
    }

    return fetch(apiAdBase + url, requestOptions);
  }

  async forgotPassword(forgotPasswordPayload: ForgotPasswordRequest) {
    return await this.sendRequestApi(
      "forgot-password",
      "POST",
      forgotPasswordPayload
    );
  }

  async getDebtorForCompassReferral(debtorId: string): Promise<Response> {
    return await this.sendRequestApi(
      `compass-referral-debtor-exclusion/debtor/${debtorId}`,
      "GET",
    );
  }

  async getGeneralSettings(id: number): Promise<Response> {
    return this.sendRequestApi(`global-settings/${id}`, "GET");
  }

  async getPreInspection(estimateNumber: string, token?: string) {
    return await this.sendRequestApi(
      `pre-inspection/${estimateNumber}`,
      "GET",
      null,
      token
    );
  }

  async getFile(name: string, path: string) {
    return await this.sendRequestApi(`file/${path}/${name}`, "GET", null);
  }

  async sendJSONFile(json: object, id: string) {
    return await this.sendRequestApi(`pre-inspection/${id}/json-file`, "PUT", {
      json,
      path: "customer_signature",
    });
  }

  async updatePreInspection(data: any, id: string) {
    return await this.sendRequestApi(`pre-inspection/${id}`, "PUT", data);
  }

  async markActionLogAsUsed(data: { estimateNumber: string; action: string }) {
    return await this.sendRequestApi(`action-logs/mark-used`, "PUT", data);
  }

  async getBudgetRevisions() {
    return await this.sendRequestIbsApi(
      `budgetRevision/c2990410-098f-5981-8eac-bab2659f4c4c`,
      "GET",
      null
    );
  }
  async getArrivalDisplayJobs() {
    return await this.sendRequestIbsApi(`GetArrivalDisplayJobs`, "GET", null);
  }
  async getBookingDays(dateRange: { start: string; end: string }) {
    return await this.sendRequestIbsApi(
      `bookingPlanner?start=${dateRange.start}&end=${dateRange.end}`,
      "GET",
      null
    );
  }
  async getBookingDropoffRange() {
    return await this.sendRequestApi(`bookingDropoffRange`, "GET", null);
  }

  async bookSlot(slot: any) {
    return await this.sendRequestApi("booking", "POST", slot);
  }
  async bookSlotUpdate(id: string, slot: any) {
    return await this.sendRequestApi("booking/" + id, "PUT", slot);
  }

  async getBookingForId(id: string) {
    return await this.sendRequestApi(
      "booking?jobExternalId=" + id,
      "GET",
      null
    );
  }

  async getAvailabilityData() {
    return await this.sendRequestApi("booking-slots", "GET").then((resp)=>resp.json());
  }

  async getCustomerData(customer: {
    externalId: string;
    name: string;
    email: string | null;
    phone:string | null;
    typeOfRequest?: string;
    estimateData?:any
  }) {
    return await this.sendRequestApi("customer/register", "POST", customer);
  }

  async requestCarbizReferral(data: ICarbizReferralPayload) {
    return await this.sendRequestApi("customer/request-carbiz-referral", "POST", data);
  }

  async adminLogin(body: {
    token:string
  }) {
    return await this.sendRequestApi("admin-login", "POST", body);
  }

  async getActionLogs(estimateId: string, type: string = ActionType.EstimateFirstTimeLogin) {
    return await this.sendRequestApi(`action-logs/${estimateId}/${type}`, "GET");
  }

  async login(username: string, estimateNumber: string, password?: string) {
    const payload: any = { username, estimateNumber };

    if (password) {
      payload.password = password; // Include password only if provided
    }

    return await this.sendRequestApi("login", "POST", payload);
  }

  async requestOTP(email: string | null, phone: string | null) {
    const payload: any = { };
    if (email) {
      payload.email = email;
    }
    if (phone) {
      payload.phone = phone;
    }
    return await this.sendRequestApi("request-otp", "POST", payload);
  }

  async verifyOTP(otp: string, userId: string) {
    const payload: any = { otp, userId };

    return await this.sendRequestApi("verify-otp", "POST", payload);
  }

  async loginMagicLink(userId: string, token: string) {
    return await this.sendRequestApi(
      `login-magic-link/${userId}/${token}`,
      "POST"
    );
  }

  async reSendEmail(userId: number) {
    return await this.sendRequestApi("active", "POST", {
      userId,
    });
  }

  sendRequestApiNotification(url: string, method: string, data: any) {
    let requestOptions: any = {
      method: method,
      headers: { "Content-Type": "text/html" },
    };
    if (data) {
      requestOptions["body"] = data;
    }
    if (this.currentUser?.auth) {
      requestOptions["headers"] = {
        ...requestOptions["headers"],
        Authorization: `Bearer ${this.currentUser.auth.token}`,
      };
    }
    return fetch(apiAdBaseUrl + url, requestOptions);
  }

  async sendCustomerChatNotification(message: string, email: string) {
    if (!allowedEmails.includes(email)) {
      return await this.sendRequestApiNotification(
        "customerChat",
        "POST",
        message
      );
    }
  }

  async sendResetPassword(data: any) {
    return await this.sendRequestAdonisApi(
      "reset-password-with-email/" + data.email,
      "POST",
      data
    );
  }

  async changePassword(data: any) {
    return await this.sendRequestAdonisApi(
      "change-password/" + this.currentUser.user.id,
      "POST",
      data
    );
  }

  async getBookingAllowance() {
    return await this.sendRequestApi("booking-allowance", "GET", null);
  }

  async getLockedDates(startDate: string, endDate: string): Promise<any> {
    const url = `getLockedBookingDates?startDate=${startDate}&endDate=${endDate}`;
    try {
      const response = await this.sendRequestApi(url, "GET", null);
      if (response.ok) {
        return await response.json();
      } else {
        throw new Error(`Failed to fetch locked dates: ${response.statusText}`);
      }
    } catch (error) {
      console.error("Error in getLockedDates API call:", error);
      throw error;
    }
  }
}

export default APIService;
